import { Localization } from "../../../common/localizable-component";
import { DatePickerLocalization } from "./date-picker-types";
import { deepmerge } from "../../../utils/deep-merge";
import { FORM_LOCALIZATION } from "../../form/constants/form-i18n";
import { cs, da, de, enUS, es, fr, hr, hu, it, nl, pl, ro, sl, sv } from "date-fns/locale";

export const DATE_PICKER_LOCALIZATION: Localization<DatePickerLocalization> = deepmerge(FORM_LOCALIZATION, {
  en: {
    dateFormat: "MM.dd.yyyy",
    locale: "en-US",
    dateRangeErrorMessage: "The date is outside the allowed date range.",
    weekendErrorMessage: "The date is on a weekend.",
    disableDatesErrorMessage: "This date is disabled.",
    nextMonth: "Next month",
    prevMonth: "Previous month",
    dateFnsLocale: enUS,
    chooseDate: "Choose date",
    openCalendar: "Open calendar",
    closeCalendar: "Close calendar"
  },
  de: {
    dateFormat: "dd.MM.yyyy",
    locale: "de-DE",
    dateRangeErrorMessage: "Das Datum liegt außerhalb des erlaubten Datumsrahmens.",
    weekendErrorMessage: "Das Datum liegt an einem Wochenende.",
    disableDatesErrorMessage: "Dieses Datum ist deaktiviert.",
    nextMonth: "Nächster Monat",
    prevMonth: "Vorheriger Monat",
    dateFnsLocale: de,
    chooseDate: "Datum wählen",
    openCalendar: "Kalender öffnen",
    closeCalendar: "Kalender schließen"
  },
  sv: {
    dateFormat: "dd.MM.yyyy",
    locale: "sv-SE",
    dateRangeErrorMessage: "Datumet ligger utanför det tillåtna datumintervallet.",
    weekendErrorMessage: "Datumet är på en helg.",
    disableDatesErrorMessage: "Detta datum är inaktiverat.",
    nextMonth: "Nästa månad",
    prevMonth: "Föregående månad",
    dateFnsLocale: sv,
    chooseDate: "Välj datum",
    openCalendar: "Öppna kalender",
    closeCalendar: "Stäng kalender"
  },
  nl: {
    dateFormat: "dd.MM.yyyy",
    locale: "nl-NL",
    dateRangeErrorMessage: "De datum valt buiten het toegestane datumbereik.",
    weekendErrorMessage: "De datum valt in een weekend.",
    disableDatesErrorMessage: "Deze datum is uitgeschakeld.",
    nextMonth: "volgende maand",
    prevMonth: "vorige maand",
    dateFnsLocale: nl,
    chooseDate: "Kies datum",
    openCalendar: "Kalender openen",
    closeCalendar: "Kalender sluiten"
  },
  it: {
    dateFormat: "gg.MM.aaaa",
    locale: "it-IT",
    dateRangeErrorMessage: "La data non rientra nell'intervallo di date consentito.",
    weekendErrorMessage: "La data ricade nel fine settimana.",
    disableDatesErrorMessage: "Questa data è disabilitata.",
    nextMonth: "Prossimo mese",
    prevMonth: "Mese scorso",
    dateFnsLocale: it,
    chooseDate: "Scegli data",
    openCalendar: "Apri calendario",
    closeCalendar: "Chiudi calendario"
  },
  hu: {
    dateFormat: "éééé.hh.nn.",
    locale: "hu-HU",
    dateRangeErrorMessage: "A dátum kívül esik a megengedett időtartományon.",
    weekendErrorMessage: "A dátum hétvégére esik.",
    disableDatesErrorMessage: "Ez a dátum le van tiltva.",
    nextMonth: "Következő hónap",
    prevMonth: "Előző hónap",
    dateFnsLocale: hu,
    chooseDate: "Dátum kiválasztása",
    openCalendar: "Naptár megnyitása",
    closeCalendar: "Naptár bezárása"
  },
  da: {
    dateFormat: "dd.MM.yyyy",
    locale: "da-DK",
    dateRangeErrorMessage: "Datoen er uden for det tilladte interval.",
    weekendErrorMessage: "Datoen er en weekend.",
    disableDatesErrorMessage: "Denne dato er deaktiveret.",
    nextMonth: "Næste måned",
    prevMonth: "Sidste måned",
    dateFnsLocale: da,
    chooseDate: "Vælg dato",
    openCalendar: "Åbn kalender",
    closeCalendar: "Luk kalender"
  },
  fr: {
    dateFormat: "MM.jj.aaaa",
    locale: "fr-FR",
    dateRangeErrorMessage: "La date est en dehors de la plage de dates autorisée.",
    weekendErrorMessage: "La date est un week-end.",
    disableDatesErrorMessage: "Cette date est désactivée.",
    nextMonth: "Mois prochain",
    prevMonth: "Mois précédent",
    dateFnsLocale: fr,
    chooseDate: "Choisir la date",
    openCalendar: "Ouvrir le calendrier",
    closeCalendar: "Fermer le calendrier"
  },
  es: {
    dateFormat: "MM.dd.aaa",
    locale: "es-ES",
    dateRangeErrorMessage: "La fecha está fuera del intervalo de fechas permitido.",
    weekendErrorMessage: "La fecha es en fin de semana.",
    disableDatesErrorMessage: "Esta fecha está desactivada.",
    nextMonth: "Próximo mes",
    prevMonth: "Mes anterior",
    dateFnsLocale: es,
    chooseDate: "Elegir fecha",
    openCalendar: "Abrir calendario",
    closeCalendar: "Cerrar calendario"
  },
  pl: {
    dateFormat: "dd/MM/rrrr",
    locale: "pl-PL",
    dateRangeErrorMessage: "Data jest poza dozwolonym zakresem dat.",
    weekendErrorMessage: "Data przypada na weekend.",
    disableDatesErrorMessage: "Ta data jest wyłączona.",
    nextMonth: "Następny miesiąc",
    prevMonth: "Poprzedni miesiąc",
    dateFnsLocale: pl,
    chooseDate: "Wybierz datę",
    openCalendar: "Otwórz kalendarz",
    closeCalendar: "Zamknij kalendarz"
  },
  ro: {
    dateFormat: "dd.MM.yyyy",
    locale: "ro-RO",
    dateRangeErrorMessage: "Data este în afara intervalului de date permis.",
    weekendErrorMessage: "Data este într-un weekend.",
    disableDatesErrorMessage: "Această dată este dezactivată.",
    nextMonth: "Luna viitoare",
    prevMonth: "Luna precedentă",
    dateFnsLocale: ro,
    chooseDate: "Alege data",
    openCalendar: "Deschide calendarul",
    closeCalendar: "Închide calendarul"
  },
  hr: {
    dateFormat: "MM.dd.gggg",
    locale: "hr-HR",
    dateRangeErrorMessage: "Datum je izvan dopuštenog raspona datuma.",
    weekendErrorMessage: "Datum je vikend.",
    disableDatesErrorMessage: "Ovaj datum je onemogućen.",
    nextMonth: "Sljedeći mjesec",
    prevMonth: "Prethodni mjesec",
    dateFnsLocale: hr,
    chooseDate: "Odaberi datum",
    openCalendar: "Otvori kalendar",
    closeCalendar: "Zatvori kalendar"
  },
  cs: {
    dateFormat: "MM.dd.rrrr",
    locale: "cs-CS",
    dateRangeErrorMessage: "Datum je mimo povolený rozsah dat.",
    weekendErrorMessage: "Datum připadá na víkend.",
    disableDatesErrorMessage: "Toto datum je zakázáno.",
    nextMonth: "Příští měsíc",
    prevMonth: "Předchozí měsíc",
    dateFnsLocale: cs,
    chooseDate: "Vyberte datum",
    openCalendar: "Otevřít kalendář",
    closeCalendar: "Zavřít kalendář"
  },
  sl: {
    dateFormat: "MM.dd.yyyy",
    locale: "sl-SL",
    dateRangeErrorMessage: "Datum je zunaj dovoljenega datumskega območja.",
    weekendErrorMessage: "Datum je ob koncu tedna.",
    disableDatesErrorMessage: "Ta datum je onemogočen.",
    nextMonth: "Naslednji mesec",
    prevMonth: "Prejšnji mesec",
    dateFnsLocale: sl,
    chooseDate: "Izberite datum",
    openCalendar: "Odpri koledar",
    closeCalendar: "Zapri koledar"
  }
});
